'use client';

/**
 * useGenericComponent Hook
 *
 * Encapsulates common logic shared by all criteria component types.
 * Handles state management, modal behavior, item updates, and Redux sync.
 *
 * This reduces ~190 lines of duplicated code per component to ~20 lines.
 */

import { useState, useCallback, useEffect } from 'react';
import { CriteriaComponentType } from 'src/redux/slices/aiListGeneratorSlice';
import { BaseSelectableItem, ConnectorType } from '../components/shared/types';

export interface UseGenericComponentParams<T extends BaseSelectableItem> {
  /** Unique component identifier */
  componentId: string;
  /** Component type (e.g., 'codes', 'payer_type') */
  type: CriteriaComponentType;
  /** Redux data field name — always 'value' for all component types */
  reduxDataField: string;
  /** Function to extract IDs from items for Redux storage */
  getItemIds: (items: T[]) => any;
  /** Initial items from props */
  initialItems: T[];
  /** Initial connector value */
  initialConnector: ConnectorType;
  /** Callback when component data changes */
  onUpdate: (componentId: string, data: any) => void;
  /** Callback to remove the component */
  onRemove: (componentId: string) => void;
  /** Whether component was created from a template placeholder */
  fromPlaceholder?: boolean;
}

export interface UseGenericComponentReturn<T extends BaseSelectableItem> {
  // State
  items: T[];
  connector: ConnectorType;
  showModal: boolean;
  searchTerm: string;
  isNewFromPlaceholder: boolean;

  // Setters
  setShowModal: (show: boolean) => void;
  setSearchTerm: (term: string) => void;

  // Handlers
  handleSave: (newItems: T[], newConnector: ConnectorType) => void;
  handleModalClose: () => void;
  handleRemoveItem: (itemToRemove: T) => void;
  handleConnectorChange: (newConnector: ConnectorType) => void;
}

/**
 * Generic hook for criteria component state management.
 *
 * Handles:
 * - State initialization from props
 * - Auto-open modal for new components
 * - External sync when items are added via slash menu
 * - Save/remove/connector change handlers with Redux updates
 * - Placeholder behavior (remove on close without selection)
 */
export function useGenericComponent<T extends BaseSelectableItem>({
  componentId,
  type,
  reduxDataField,
  getItemIds,
  initialItems,
  initialConnector,
  onUpdate,
  onRemove,
  fromPlaceholder = false,
}: UseGenericComponentParams<T>): UseGenericComponentReturn<T> {
  // Core state
  const [items, setItems] = useState<T[]>(initialItems);
  const [connector, setConnector] = useState<ConnectorType>(initialConnector);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Track if this is a new component from a placeholder that hasn't saved yet
  const [isNewFromPlaceholder, setIsNewFromPlaceholder] = useState(
    fromPlaceholder && initialItems.length === 0
  );

  // Auto-open modal if starting with no items (new component)
  useEffect(() => {
    if (initialItems.length === 0) {
      setShowModal(true);
    }
  }, []); // Only run on mount

  // Sync when external items are added (slash menu merge)
  useEffect(() => {
    if (initialItems.length > items.length) {
      setItems(initialItems);
    }
  }, [initialItems.length]);

  // Build Redux update payload
  const buildUpdatePayload = useCallback(
    (newItems: T[], newConnector: ConnectorType) => ({
      type,
      [reduxDataField]: getItemIds(newItems),
      ui_metadata: newItems,
      connector: newConnector,
    }),
    [type, reduxDataField, getItemIds]
  );

  // Handle save from modal
  const handleSave = useCallback(
    (newItems: T[], newConnector: ConnectorType) => {
      if (!newItems || newItems.length === 0) {
        onRemove(componentId);
        setShowModal(false);
        return;
      }

      setItems(newItems);
      setConnector(newConnector);
      setIsNewFromPlaceholder(false);

      onUpdate(componentId, buildUpdatePayload(newItems, newConnector));
      setShowModal(false);
    },
    [componentId, onUpdate, onRemove, buildUpdatePayload]
  );

  // Handle modal close - if from placeholder with no data, remove component
  const handleModalClose = useCallback(() => {
    setShowModal(false);
    if (isNewFromPlaceholder && items.length === 0) {
      onRemove(componentId);
    }
  }, [isNewFromPlaceholder, items.length, onRemove, componentId]);

  // Handle removing a single item
  const handleRemoveItem = useCallback(
    (itemToRemove: T) => {
      const newItems = items.filter((item) => item.id !== itemToRemove.id);

      if (newItems.length === 0) {
        onRemove(componentId);
      } else {
        setItems(newItems);
        onUpdate(componentId, buildUpdatePayload(newItems, connector));
      }
    },
    [items, connector, componentId, onUpdate, onRemove, buildUpdatePayload]
  );

  // Handle connector change
  const handleConnectorChange = useCallback(
    (newConnector: ConnectorType) => {
      setConnector(newConnector);
      onUpdate(componentId, buildUpdatePayload(items, newConnector));
    },
    [items, componentId, onUpdate, buildUpdatePayload]
  );

  return {
    // State
    items,
    connector,
    showModal,
    searchTerm,
    isNewFromPlaceholder,

    // Setters
    setShowModal,
    setSearchTerm,

    // Handlers
    handleSave,
    handleModalClose,
    handleRemoveItem,
    handleConnectorChange,
  };
}

export default useGenericComponent;
